import type { RuntimeSurface } from '@boox/contracts';

export type DynamicRuntimeRouteAlias = {
  browserRoute: string;
  runtimeRoute?: string;
  surface: RuntimeSurface;
  title?: string;
  categoryId?: string;
};

export type DynamicRuntimeBinding = {
  categoryId: string;
  categoryLabel?: string;
  categoryIcon?: string;
  categoryZone?: 'core' | 'verticals' | 'support' | 'admin';
  categoryOrder?: number;
  requiredPermission?: string;
  requiredModule?: string;
  module: string;
  profileTemplateCode?: string;
  defaultRoute: string;
  routePrefixes: string[];
  pwaRoutePrefixes?: string[];
  portalRoutePrefixes?: string[];
  chatContext?: {
    module?: string;
    pageKeyPrefix?: string;
    selfServicePageKeyPrefix?: string;
    selfServicePathPrefixes?: string[];
  };
  aliases?: DynamicRuntimeRouteAlias[];
};

export const DYNAMIC_RUNTIME_BINDINGS: DynamicRuntimeBinding[] = [
  {
    categoryId: 'tm-runtime',
    categoryLabel: 'TM Transport',
    categoryIcon: 'truck',
    categoryZone: 'verticals',
    categoryOrder: 5.5,
    requiredPermission: 'job:read',
    requiredModule: 'TM',
    module: 'tm',
    profileTemplateCode: 'tm-profile',
    defaultRoute: '/tm',
    routePrefixes: ['/tm', '/pwa/tm', '/portal/tm'],
    pwaRoutePrefixes: ['/pwa/tm'],
    portalRoutePrefixes: ['/portal/tm'],
    aliases: [
      { browserRoute: '/tm/share-preview', surface: 'share', title: 'Shared Transport Job' },
      { browserRoute: '/tm/portal-preview', runtimeRoute: '/portal/tm', surface: 'self-service', title: 'Customer Portal' },
      { browserRoute: '/tm/rates', runtimeRoute: '/tm/billing', surface: 'web', title: 'TM Billing', categoryId: 'rate' },
      { browserRoute: '/tm/costing', runtimeRoute: '/tm/billing', surface: 'web', title: 'TM Billing', categoryId: 'costing' },
      { browserRoute: '/tm/billing', surface: 'web', title: 'TM Billing', categoryId: 'billing' },
      { browserRoute: '/tm/quotation', runtimeRoute: '/tm/billing', surface: 'web', title: 'TM Billing', categoryId: 'quotation' },
      { browserRoute: '/tm/billing/costing', runtimeRoute: '/tm/billing', surface: 'web', title: 'TM Billing', categoryId: 'costing' },
      { browserRoute: '/tm/billing/billing', runtimeRoute: '/tm/billing', surface: 'web', title: 'TM Billing', categoryId: 'billing' },
      { browserRoute: '/tm/billing/quotation', runtimeRoute: '/tm/billing', surface: 'web', title: 'TM Billing', categoryId: 'quotation' },
      { browserRoute: '/tm/settlement', runtimeRoute: '/tm/billing', surface: 'web', title: 'TM Billing', categoryId: 'billing' },
    ],
  },
  {
    categoryId: 'ct',
    categoryLabel: 'CT Container Trucking',
    categoryIcon: 'container',
    categoryZone: 'verticals',
    categoryOrder: 41,
    requiredPermission: 'ct.read',
    requiredModule: 'CT',
    module: 'ct',
    profileTemplateCode: 'ct-profile',
    defaultRoute: '/ct',
    routePrefixes: ['/ct', '/pwa/ct', '/share/ct'],
    pwaRoutePrefixes: ['/pwa/ct'],
    aliases: [
      { browserRoute: '/ct/share-preview', surface: 'share', title: 'Shared Container Job' },
      { browserRoute: '/share/ct/demo', surface: 'share', title: 'Shared Container Job' },
    ],
  },
  {
    categoryId: 'wg',
    categoryLabel: 'WG 3PL Warehouse',
    categoryIcon: 'forklift',
    categoryZone: 'verticals',
    categoryOrder: 41.5,
    requiredPermission: 'wg.read',
    requiredModule: 'WG',
    module: 'wg',
    profileTemplateCode: 'wg-profile',
    defaultRoute: '/wg',
    routePrefixes: ['/wg', '/pwa/wg', '/share/wg', '/portal/wg'],
    pwaRoutePrefixes: ['/pwa/wg'],
    portalRoutePrefixes: ['/portal/wg'],
    aliases: [
      { browserRoute: '/wg/stock', runtimeRoute: '/wg/inventory', surface: 'web', title: 'Inventory' },
      { browserRoute: '/wg/share-preview', surface: 'share', title: 'Shared 3PL Warehouse Order' },
      { browserRoute: '/share/wg/demo', surface: 'share', title: 'Shared 3PL Warehouse Order' },
    ],
  },
  {
    categoryId: 'wm',
    categoryLabel: 'WM',
    categoryIcon: 'warehouse',
    categoryZone: 'verticals',
    categoryOrder: 42,
    requiredPermission: 'wm.read',
    requiredModule: 'WM',
    module: 'wm',
    profileTemplateCode: 'wm-yard-bundle-profile',
    defaultRoute: '/wm',
    routePrefixes: ['/wm', '/pwa/wm', '/share/wm', '/portal/wm'],
    pwaRoutePrefixes: ['/pwa/wm'],
    portalRoutePrefixes: ['/portal/wm'],
    aliases: [
      { browserRoute: '/wms', runtimeRoute: '/wm', surface: 'web', title: 'WM Overview' },
      { browserRoute: '/wms/inbound', runtimeRoute: '/wm/inbound', surface: 'web', title: 'Inbound' },
      { browserRoute: '/wm/inventory', runtimeRoute: '/wm/inventory', surface: 'web', title: 'Inventory' },
      { browserRoute: '/wm/lots', runtimeRoute: '/wm/inventory', surface: 'web', title: 'Lot Search' },
      { browserRoute: '/wm/shipping', runtimeRoute: '/wm/outbound', surface: 'web', title: 'Shipping' },
      { browserRoute: '/wms/inventory', runtimeRoute: '/wm/inventory', surface: 'web', title: 'Inventory' },
      { browserRoute: '/wms/outbound', runtimeRoute: '/wm/outbound', surface: 'web', title: 'Outbound' },
      { browserRoute: '/wms/shipping', runtimeRoute: '/wm/outbound', surface: 'web', title: 'Shipping' },
      { browserRoute: '/wms/work-orders', runtimeRoute: '/wm/work', surface: 'web', title: 'Warehouse Tasks' },
      { browserRoute: '/wms/stock-take', runtimeRoute: '/wm/stock-take', surface: 'web', title: 'Stock Take' },
      { browserRoute: '/wms/inspection', runtimeRoute: '/wm/inspection', surface: 'web', title: 'Inspection' },
      { browserRoute: '/wms/tasks', runtimeRoute: '/wm/tasks', surface: 'web', title: 'My Tasks' },
      { browserRoute: '/wms/documents', runtimeRoute: '/wm/documents', surface: 'web', title: 'Shared Documents' },
      { browserRoute: '/wms/reports', runtimeRoute: '/wm/reports', surface: 'web', title: 'Reports' },
      { browserRoute: '/wms/master-data', runtimeRoute: '/wm/master-data', surface: 'web', title: 'Master Data' },
      { browserRoute: '/wms/setup', runtimeRoute: '/wm/setup', surface: 'web', title: 'Setup' },
      { browserRoute: '/wm/billing', runtimeRoute: '/wm/costing', surface: 'web', title: 'Billing / Settlement' },
      { browserRoute: '/wm/settlement', runtimeRoute: '/wm/costing', surface: 'web', title: 'Settlement' },
      { browserRoute: '/wms/billing', runtimeRoute: '/wm/costing', surface: 'web', title: 'Billing / Settlement' },
      { browserRoute: '/wms/settlement', runtimeRoute: '/wm/costing', surface: 'web', title: 'Settlement' },
      { browserRoute: '/pwa/wm/inbound', runtimeRoute: '/wm/inbound', surface: 'web', title: 'Inbound' },
      { browserRoute: '/wm/share-preview', surface: 'share', title: 'Shared Warehouse Job' },
      { browserRoute: '/wm/customer-inventory', runtimeRoute: '/portal/wm/inventory', surface: 'self-service', title: 'Customer Inventory' },
      { browserRoute: '/share/wm/demo', surface: 'share', title: 'Shared Warehouse Job' },
    ],
  },
  {
    categoryId: 'cf',
    categoryLabel: 'CF CFS Warehouse',
    categoryIcon: 'truck',
    categoryZone: 'verticals',
    categoryOrder: 43,
    requiredPermission: 'cf.read',
    requiredModule: 'CF',
    module: 'cf',
    profileTemplateCode: 'cf-profile',
    defaultRoute: '/cf',
    routePrefixes: ['/cf', '/pwa/cf', '/share/cf'],
    pwaRoutePrefixes: ['/pwa/cf'],
    aliases: [
      { browserRoute: '/cf/cashier/timeslots', runtimeRoute: '/cf/work', surface: 'web', title: 'Timeslot Booking', categoryId: 'timeslots-booking' },
      { browserRoute: '/cf/cashier/cargo-release-new', runtimeRoute: '/cf/work', surface: 'web', title: 'New Cargo Release', categoryId: 'cargo-release-new' },
      { browserRoute: '/cf/cashier/cargo-release', runtimeRoute: '/cf/work', surface: 'web', title: 'Cargo Release List', categoryId: 'cargo-release' },
      { browserRoute: '/cf/cashier/cargo-receipts', runtimeRoute: '/cf/work', surface: 'web', title: 'Cargo Receipt List', categoryId: 'cargo-receipts' },
      { browserRoute: '/cf/operations/import-containers', runtimeRoute: '/cf/work', surface: 'web', title: 'Import Container', categoryId: 'import-container' },
      { browserRoute: '/cf/operations/export-containers', runtimeRoute: '/cf/work', surface: 'web', title: 'Export Container', categoryId: 'export-stuffing' },
      { browserRoute: '/cf/operations/coload', runtimeRoute: '/cf/work', surface: 'web', title: 'Coload', categoryId: 'coload' },
      { browserRoute: '/cf/reports/collection', runtimeRoute: '/cf/reports', surface: 'web', title: 'Collection Report', categoryId: 'collection-report' },
      { browserRoute: '/cf/reports/inventory', runtimeRoute: '/cf/reports', surface: 'web', title: 'Inventory Report', categoryId: 'inventory-report' },
      { browserRoute: '/cf/reports/container-schedule', runtimeRoute: '/cf/reports', surface: 'web', title: 'Container Schedule', categoryId: 'container-schedule' },
      { browserRoute: '/cf/share-preview', surface: 'share', title: 'Shared CFS Work' },
      { browserRoute: '/share/cf/demo', surface: 'share', title: 'Shared CFS Work' },
    ],
  },
  {
    categoryId: 'fm',
    categoryLabel: 'FM',
    categoryIcon: 'ship',
    categoryZone: 'verticals',
    categoryOrder: 44,
    requiredPermission: 'fm.read',
    requiredModule: 'FM',
    module: 'fm',
    profileTemplateCode: 'fm-profile',
    defaultRoute: '/fm',
    routePrefixes: ['/fm', '/pwa/fm', '/share/fm', '/portal/fm'],
    pwaRoutePrefixes: ['/pwa/fm'],
    portalRoutePrefixes: ['/portal/fm'],
    aliases: [
      { browserRoute: '/fm/import', runtimeRoute: '/fm/shipments', surface: 'web', title: 'Import House Control', categoryId: 'import' },
      { browserRoute: '/fm/export', runtimeRoute: '/fm/shipments', surface: 'web', title: 'Export House Control', categoryId: 'export' },
      { browserRoute: '/fm/transhipment', runtimeRoute: '/fm/shipments', surface: 'web', title: 'Transhipment', categoryId: 'transhipment' },
      { browserRoute: '/fm/cross-trade', runtimeRoute: '/fm/shipments', surface: 'web', title: 'Cross Trade', categoryId: 'cross-trade' },
      { browserRoute: '/fm/local', runtimeRoute: '/fm/shipments', surface: 'web', title: 'Local', categoryId: 'local' },
      { browserRoute: '/fm/local-handling', runtimeRoute: '/fm/shipments', surface: 'web', title: 'Local Handling', categoryId: 'local' },
      { browserRoute: '/fm/consol', runtimeRoute: '/fm/shipments', surface: 'web', title: 'Consol', categoryId: 'consol' },
      { browserRoute: '/fm/air', runtimeRoute: '/fm/shipments', surface: 'web', title: 'Air', categoryId: 'air' },
      { browserRoute: '/fm/planner', runtimeRoute: '/fm/schedule', surface: 'web', title: 'Schedule' },
      { browserRoute: '/fm/documents', surface: 'web', title: 'Document Control', categoryId: 'documents' },
      { browserRoute: '/fm/consols', surface: 'web', title: 'Consol Workbench', categoryId: 'consols' },
      { browserRoute: '/fm/tariffs', surface: 'web', title: 'Tariffs', categoryId: 'tariffs' },
      { browserRoute: '/fm/quotations', surface: 'web', title: 'Quotations', categoryId: 'quotations' },
      { browserRoute: '/fm/agent-desk', surface: 'web', title: 'Agent Desk', categoryId: 'agent-desk' },
      { browserRoute: '/fm/contractors', surface: 'web', title: 'Contractor Desk', categoryId: 'contractors' },
      { browserRoute: '/fm/prints', surface: 'web', title: 'Print Center', categoryId: 'prints' },
      { browserRoute: '/fm/close-desk', surface: 'web', title: 'Close Desk', categoryId: 'close-desk' },
      { browserRoute: '/fm/portal-preview', runtimeRoute: '/portal/fm', surface: 'self-service', title: 'FM Partner Portal' },
      { browserRoute: '/fm/share-preview', surface: 'share', title: 'Shared Freight Job' },
      { browserRoute: '/share/fm/demo', surface: 'share', title: 'Shared Freight Job' },
    ],
  },
  {
    categoryId: 'sc',
    categoryLabel: 'SC Supply Chain',
    categoryIcon: 'layers',
    categoryZone: 'verticals',
    categoryOrder: 45,
    requiredPermission: 'sc.read',
    requiredModule: 'SC',
    module: 'sc',
    defaultRoute: '/sc',
    routePrefixes: ['/sc', '/pwa/sc', '/share/sc', '/portal/sc'],
    pwaRoutePrefixes: ['/pwa/sc'],
    portalRoutePrefixes: ['/portal/sc'],
    aliases: [
      { browserRoute: '/scm/sales-orders', runtimeRoute: '/sc/sales-orders', surface: 'web', title: 'Sales Orders' },
      { browserRoute: '/scm/purchase-orders', runtimeRoute: '/sc/purchase-orders', surface: 'web', title: 'Purchase Orders' },
      { browserRoute: '/scm/quotations', runtimeRoute: '/sc/quotations', surface: 'web', title: 'Quotations' },
      { browserRoute: '/scm/rfq', runtimeRoute: '/sc/rfq', surface: 'web', title: 'RFQ' },
      { browserRoute: '/scm/products', runtimeRoute: '/sc/master-data', surface: 'web', title: 'Master Data' },
      { browserRoute: '/scm/suppliers', runtimeRoute: '/sc/master-data', surface: 'web', title: 'Master Data' },
      { browserRoute: '/scm/salespersons', runtimeRoute: '/sc/master-data', surface: 'web', title: 'Master Data' },
      { browserRoute: '/scm/inventory', runtimeRoute: '/sc/inventory', surface: 'web', title: 'Inventory Control' },
      { browserRoute: '/scm/stock-movements', runtimeRoute: '/sc/inventory', surface: 'web', title: 'Inventory Control' },
      { browserRoute: '/scm/fulfillment', runtimeRoute: '/sc/workflow', surface: 'web', title: 'Workflow' },
      { browserRoute: '/scm/shipment-tracking', runtimeRoute: '/sc/workflow', surface: 'web', title: 'Workflow' },
      { browserRoute: '/scm/costing', runtimeRoute: '/sc/costing', surface: 'web', title: 'Costing' },
      { browserRoute: '/scm/reports', runtimeRoute: '/sc/reports', surface: 'web', title: 'Reports' },
      { browserRoute: '/scm', runtimeRoute: '/sc', surface: 'web', title: 'SC Overview' },
      { browserRoute: '/pwa/sc/tasks', surface: 'pwa', title: 'SC Mobile Tasks' },
      { browserRoute: '/sc/portal-preview', runtimeRoute: '/portal/sc', surface: 'self-service', title: 'Distributor Portal' },
      { browserRoute: '/sc/share-preview', surface: 'share', title: 'Shared Order View' },
      { browserRoute: '/share/sc/demo', surface: 'share', title: 'Shared Order View' },
    ],
  },
  {
    categoryId: 'mt',
    categoryLabel: 'MT Maritime',
    categoryIcon: 'anchor',
    categoryZone: 'verticals',
    categoryOrder: 46,
    requiredPermission: 'mt.read',
    requiredModule: 'MT',
    module: 'mt',
    profileTemplateCode: 'mt-profile',
    defaultRoute: '/mt',
    routePrefixes: ['/mt', '/share/mt', '/portal/mt'],
    portalRoutePrefixes: ['/portal/mt'],
    aliases: [
      { browserRoute: '/maritime-desk', runtimeRoute: '/mt', surface: 'web', title: 'MT Maritime' },
      { browserRoute: '/shipping-agency', runtimeRoute: '/mt', surface: 'web', title: 'MT Maritime' },
      { browserRoute: '/liner', runtimeRoute: '/mt', surface: 'web', title: 'MT Maritime' },
      { browserRoute: '/mt/portal-preview', runtimeRoute: '/portal/mt', surface: 'self-service', title: 'MT Partner Portal' },
      { browserRoute: '/mt/share-preview', surface: 'share', title: 'Shared Maritime Work' },
      { browserRoute: '/share/mt/demo', surface: 'share', title: 'Shared Maritime Work' },
    ],
  },
  {
    categoryId: 'ac',
    categoryLabel: 'AC Accounting',
    categoryIcon: 'banknote',
    categoryZone: 'support',
    categoryOrder: 47,
    requiredPermission: 'acc.read',
    requiredModule: 'AC',
    module: 'ac',
    profileTemplateCode: 'ac-profile',
    defaultRoute: '/ac',
    routePrefixes: ['/ac', '/pwa/ac', '/share/ac'],
    pwaRoutePrefixes: ['/pwa/ac'],
    aliases: [
      { browserRoute: '/acc', runtimeRoute: '/ac', surface: 'web', title: 'AC Accounting' },
      { browserRoute: '/pwa/acc', runtimeRoute: '/pwa/ac', surface: 'pwa', title: 'AC Mobile Tasks' },
      { browserRoute: '/share/acc', runtimeRoute: '/share/ac', surface: 'share', title: 'Shared Statement' },
      { browserRoute: '/ac/share-preview', surface: 'share', title: 'Shared Statement' },
      { browserRoute: '/acc/share-preview', runtimeRoute: '/ac/share-preview', surface: 'share', title: 'Shared Statement' },
      { browserRoute: '/share/ac/demo', surface: 'share', title: 'Shared Statement' },
      { browserRoute: '/share/acc/demo', runtimeRoute: '/share/ac/demo', surface: 'share', title: 'Shared Statement' },
    ],
  },
  {
    categoryId: 'rm',
    categoryLabel: 'BM Business Management',
    categoryIcon: 'user-check',
    categoryZone: 'core',
    categoryOrder: 48,
    requiredPermission: 'crm.read',
    requiredModule: 'RM',
    module: 'rm',
    profileTemplateCode: 'rm-profile',
    defaultRoute: '/rm',
    routePrefixes: ['/rm', '/share/rm', '/portal/rm'],
    portalRoutePrefixes: ['/portal/rm'],
    aliases: [
      { browserRoute: '/crm', runtimeRoute: '/rm', surface: 'web', title: 'BM Business Management' },
      { browserRoute: '/rm/portal-preview', runtimeRoute: '/portal/rm', surface: 'self-service', title: 'BM Partner Portal' },
      { browserRoute: '/rm/share-preview', surface: 'share', title: 'Shared Relationship View' },
      { browserRoute: '/share/rm/demo', surface: 'share', title: 'Shared Relationship View' },
    ],
  },
  {
    categoryId: 'ai',
    categoryLabel: 'AI',
    categoryIcon: 'zap',
    categoryZone: 'core',
    categoryOrder: 49,
    requiredPermission: 'ai.read',
    requiredModule: 'AI',
    module: 'ai',
    profileTemplateCode: 'ai-profile',
    defaultRoute: '/ai',
    routePrefixes: ['/ai'],
    aliases: [
      { browserRoute: '/aiw', runtimeRoute: '/ai', surface: 'web', title: 'AI' },
      { browserRoute: '/aic', runtimeRoute: '/ai', surface: 'web', title: 'AI' },
    ],
  },
];

const ROUTE_TITLE_OVERRIDES: Record<string, string> = {
  '/portal/tm': 'Portal',
  '/portal/fm': 'FM Partner Portal',
  '/fm/portal-preview': 'FM Partner Portal',
  '/tm/portal-preview': 'Customer Portal',
  '/tm/jobs': 'Job Orders',
  '/tm/trips': 'Trips',
  '/tm/billing': 'TM Billing',
  '/tm/rates': 'TM Billing',
  '/tm/costing': 'TM Billing',
  '/tm/quotation': 'TM Billing',
  '/tm/billing/costing': 'TM Billing',
  '/tm/billing/billing': 'TM Billing',
  '/tm/billing/quotation': 'TM Billing',
  '/tm/settlement': 'TM Billing',
  '/tm/planner': 'Planner',
  '/tm/reports': 'Reports',
  '/tm/master-data': 'Master Data',
  '/tm/events': 'Event Log',
  '/ct/jobs': 'Job Orders',
  '/ct/trips': 'Containers & Trips',
  '/ct/pricing': 'Pricing',
  '/ct/costing': 'Costing',
  '/ct/billing': 'Commercial Workspace',
  '/ct/planner': 'Planner',
  '/ct/reports': 'Reports',
  '/ct/master-data': 'Master Data',
  '/ct/events': 'Event Log',
  '/wg': 'WG Overview',
  '/wg/exceptions': 'Exceptions',
  '/wg/orders': 'Orders',
  '/wg/ops': 'Warehouse Ops',
  '/wg/inventory': 'Inventory',
  '/wg/commercial': 'Commercial',
  '/wg/partners': 'Partners',
  '/wg/master-data': 'Master Data',
  '/wg/reports': 'Reports',
  '/pwa/wg': 'Today',
  '/pwa/wg/scan': 'Scan',
  '/pwa/wg/receive': 'Receive',
  '/pwa/wg/pick': 'Pick',
  '/pwa/wg/more': 'More',
  '/pwa/wg/tasks': 'PWA Tasks',
  '/wm/jobs': 'Jobs',
  '/wm/orders': 'WMS v1 Orders',
  '/wm/inbound': 'Inbound',
  '/wm/outbound': 'Outbound',
  '/wm/container-truck': 'Lot List',
  '/wm/inventory': 'Inventory',
  '/wm/lot-search': 'Lot Search',
  '/wm/bundle-capture': 'Bundle Capture',
  '/wm/documents': 'Documents',
  '/wm/planner': 'Planner',
  '/wm/costing': 'WM Costing',
  '/wm/billing': 'Billing / Settlement',
  '/wm/settlement': 'Settlement',
  '/wm/stock-take': 'Stock Take',
  '/wm/customer-inventory': 'Customer Inventory',
  '/portal/wm/inventory': 'Customer Inventory',
  '/wm/reports': 'Reports',
  '/wm/master-data': 'Master Data',
  '/wm/warehouse-setup': 'Warehouse Setup',
  '/wm/events': 'Event Log',
  '/cf/work': 'CFS Work',
  '/cf/billing': 'Commercial Workspace',
  '/cf/execution': 'Operations Board',
  '/cf/planner': 'Daily Schedule',
  '/cf/reports': 'Reports',
  '/cf/master-data': 'Master Data',
  '/cf/events': 'Event Log',
  '/fm/jobs': 'Job Orders',
  '/fm/import': 'Import House Control',
  '/fm/export': 'Export House Control',
  '/fm/transhipment': 'Transhipment',
  '/fm/cross-trade': 'Cross Trade',
  '/fm/local': 'Local',
  '/fm/local-handling': 'Local Handling',
  '/fm/consol': 'Consol',
  '/fm/air': 'Air',
  '/fm/schedule': 'Schedule',
  '/fm/planner': 'Schedule',
  '/fm/documents': 'Document Control',
  '/fm/consols': 'Consol Workbench',
  '/fm/tariffs': 'Tariffs',
  '/fm/quotations': 'Quotations',
  '/fm/agent-desk': 'Agent Desk',
  '/fm/contractors': 'Contractor Desk',
  '/fm/prints': 'Print Center',
  '/fm/close-desk': 'Close Desk',
  '/fm/integrations/haulier': 'Haulier View',
  '/fm/integrations/cfs': 'CFS View',
  '/fm/integrations/transport': 'Transport View',
  '/fm/integrations/portnet': 'Portnet View',
  '/fm/reports': 'Reports',
  '/fm/master-data': 'Master Data',
  '/fm/events': 'Event Log',
  '/portal/sc': 'Distributor Portal',
  '/sc/orders': 'Orders',
  '/sc/sales-orders': 'Sales Orders',
  '/sc/purchase-orders': 'Purchase Orders',
  '/sc/promise': 'Promise',
  '/sc/quotations': 'Quotations',
  '/sc/rfq': 'RFQ',
  '/sc/inventory': 'Inventory Control',
  '/sc/replenishment': 'Replenishment',
  '/sc/costing': 'Costing',
  '/sc/billing': 'Billing',
  '/sc/workflow': 'Workflow',
  '/sc/planner': 'Planner',
  '/sc/exceptions': 'Exceptions',
  '/sc/master-data': 'Master Data',
  '/sc/reports': 'Reports',
  '/sc/events': 'Event Log',
  '/mt/port-calls': 'Port Calls',
  '/mt/bookings': 'Bookings',
  '/mt/releases': 'Release Desk',
  '/mt/documentation': 'Documentation',
  '/mt/tally': 'Tally Control',
  '/mt/reports': 'Reports',
  '/mt/master-data': 'Master Data',
  '/mt/events': 'Event Log',
  '/ac/ar': 'Accounts Receivable',
  '/ac/ap': 'Accounts Payable',
  '/ac/receipts': 'Receipts',
  '/ac/payments': 'Payments',
  '/ac/reports': 'Reports',
  '/ac/master-data': 'Master Data',
  '/ac/events': 'Event Log',
  '/rm/accounts': 'Accounts',
  '/rm/contacts': 'Contacts',
  '/rm/today': 'Daily Schedule',
  '/rm/signals': 'Signals',
  '/rm/pipeline': 'Pipeline',
  '/rm/quotations': 'Quotations',
  '/rm/follow-ups': 'Follow-ups',
  '/rm/recovery': 'Recovery',
  '/rm/incentives': 'Incentives',
  '/rm/erp-bridge': 'ERP Bridge',
  '/rm/reports': 'Reports',
  '/ai/inbox': 'AI Inbox',
  '/ai/agents': 'AI Agents',
  '/ai/workflows': 'AI Workflows',
  '/ai/reports': 'AI Reports',
};

function normalizePath(pathname: string): string {
  const [withoutHash] = pathname.split('#');
  const [withoutQuery] = (withoutHash ?? pathname).split('?');
  return withoutQuery || '/';
}

function suffixFromPath(pathname: string): string {
  const queryIndex = pathname.search(/[?#]/);
  return queryIndex >= 0 ? pathname.slice(queryIndex) : '';
}

function matchesRoutePrefix(normalizedPath: string, prefix: string): boolean {
  return normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`);
}

function matchesAliasBrowserRoute(normalizedPath: string, browserRoute: string): boolean {
  return normalizedPath === browserRoute || normalizedPath.startsWith(`${browserRoute}/`);
}

function matchesDynamicRuntimeBinding(normalizedPath: string, binding: DynamicRuntimeBinding): boolean {
  if (binding.routePrefixes.some((prefix) => matchesRoutePrefix(normalizedPath, prefix))) return true;
  return binding.aliases?.some((alias) => matchesAliasBrowserRoute(normalizedPath, alias.browserRoute)) ?? false;
}

function overviewTitleForDynamicRuntimeBinding(binding: DynamicRuntimeBinding): string {
  if (binding.module === 'rm' || binding.requiredModule === 'RM' || binding.requiredModule === 'BM') return 'BM Overview';
  return `${binding.requiredModule ?? binding.module.toUpperCase()} Overview`;
}

export function fmRouteForLegacyFmsPath(pathname: string): string {
  const suffix = suffixFromPath(pathname);
  const normalized = normalizePath(pathname);
  const parts = normalized.split('/').filter(Boolean);
  const page = parts[1] ?? '';
  const rest = parts.slice(2).join('/');
  const mappedPath = (() => {
    if (normalized === '/fms') return '/fm';
    if (page === 'ocean-import') return '/fm/import';
    if (page === 'ocean-export') return '/fm/export';
    if (page === 'transhipment') return '/fm/transhipment';
    if (page === 'shipments') return rest ? `/fm/shipments/${rest}` : '/fm/shipments';
    if (page === 'documents') return '/fm/documents';
    if (page === 'rates') return '/fm/tariffs';
    if (page === 'setup') return '/fm/master-data';
    if (page === 'port-system') return '/fm/integrations/portnet';
    if (page === 'consol') return '/fm/consols';
    if (page === 'air') return '/fm/air';
    if (page === 'cross-trade') return '/fm/cross-trade';
    if (page === 'local-handling') return '/fm/local-handling';
    if (page === 'billing') return '/fm/billing';
    if (page === 'costing' || page === 'accounting') return '/fm/billing';
    if (page === 'reports') return '/fm/reports';
    if (page === 'containers' || page === 'cargo') return '/fm/shipments';
    if (page === 'schedule') return '/fm/schedule';
    return '/fm';
  })();
  return `${mappedPath}${suffix}`;
}

export function dynamicRuntimeBindingForPath(pathname: string): DynamicRuntimeBinding | undefined {
  const normalized = normalizePath(pathname);
  return DYNAMIC_RUNTIME_BINDINGS.find((binding) => matchesDynamicRuntimeBinding(normalized, binding));
}

export function dynamicRuntimeBindingForCategory(categoryId: string | undefined): DynamicRuntimeBinding | undefined {
  if (!categoryId) return undefined;
  const normalized = categoryId.toLowerCase();
  const canonical = normalized === 'acc' ? 'ac'
    : normalized === 'crm' ? 'rm'
      : normalized === 'aiw' || normalized === 'aic' ? 'ai'
        : normalized === 'tm' ? 'tm-runtime'
          : ['sas', 'liner', 'maritime-desk', 'shipping-agency'].includes(normalized) ? 'mt'
            : normalized;
  return DYNAMIC_RUNTIME_BINDINGS.find((binding) => binding.categoryId === canonical);
}

export function isPathInDynamicRuntimeBinding(pathname: string, binding: DynamicRuntimeBinding): boolean {
  const normalized = normalizePath(pathname);
  return matchesDynamicRuntimeBinding(normalized, binding);
}

export function surfaceForDynamicRuntimePath(pathname: string, binding: DynamicRuntimeBinding): RuntimeSurface {
  const normalized = normalizePath(pathname);
  const alias = binding.aliases?.find((item) => item.browserRoute === normalized);
  if (alias) return alias.surface;
  if (binding.routePrefixes.some((prefix) => prefix.startsWith('/share/') && (normalized === prefix || normalized.startsWith(`${prefix}/`)))) return 'share';
  if (binding.pwaRoutePrefixes?.some((prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`))) return 'pwa';
  if (binding.portalRoutePrefixes?.some((prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`))) return 'self-service';
  if (normalized.startsWith('/portal/')) return 'self-service';
  return 'web';
}

export function runtimeRouteForDynamicRuntimePath(pathname: string, binding: DynamicRuntimeBinding): string {
  const normalized = normalizePath(pathname);
  const alias = binding.aliases
    ?.filter((item) => matchesAliasBrowserRoute(normalized, item.browserRoute))
    .sort((left, right) => right.browserRoute.length - left.browserRoute.length)[0];
  if (!alias?.runtimeRoute) return normalized;
  const remainder = normalized.slice(alias.browserRoute.length);
  return `${alias.runtimeRoute}${remainder}`;
}

export function browserRouteForDynamicRuntimeRoute(route: string, binding: DynamicRuntimeBinding | undefined): string {
  if (!binding) return route;
  const normalized = normalizePath(route);
  const suffix = suffixFromPath(route);
  const alias = binding.aliases?.find((item) => (
    item.runtimeRoute
    && !item.categoryId
    && (normalized === item.runtimeRoute || normalized.startsWith(`${item.runtimeRoute}/`))
  ));
  if (alias) {
    if (binding.categoryId === 'wm' && normalized.startsWith('/wm')) return route;
    const remainder = normalized.slice(alias.runtimeRoute!.length);
    return `${alias.browserRoute}${remainder}${suffix}`;
  }

  return route;
}

export function shareContextRefForDynamicRuntimePath(pathname: string, binding: DynamicRuntimeBinding): string | undefined {
  const normalized = normalizePath(pathname);
  const sharePrefix = binding.routePrefixes
    .filter((prefix) => prefix.startsWith('/share/') && (normalized === prefix || normalized.startsWith(`${prefix}/`)))
    .sort((a, b) => b.length - a.length)[0];
  if (!sharePrefix) return undefined;
  const remainder = normalized.slice(sharePrefix.length).replace(/^\/+/, '');
  return remainder ? decodeURIComponent(remainder.split('/').filter(Boolean).at(-1) ?? remainder) : undefined;
}

export function titleForDynamicRuntimePath(pathname: string, binding: DynamicRuntimeBinding): string {
  const normalized = normalizePath(pathname);
  if (normalized === normalizePath(binding.defaultRoute)) return overviewTitleForDynamicRuntimeBinding(binding);
  return binding.aliases?.find((item) => item.browserRoute === normalized)?.title
    ?? ROUTE_TITLE_OVERRIDES[normalized]
    ?? binding.categoryLabel
    ?? binding.module.toUpperCase();
}
